export type CameraFacingMode = 'user' | 'environment';

const cameraLabelHints: Record<CameraFacingMode, RegExp> = {
  user: /front|user|face|selfie/i,
  environment: /back|rear|environment|world/i,
};

export const getNextFacingMode = (current: CameraFacingMode): CameraFacingMode =>
  current === 'user' ? 'environment' : 'user';

async function getVideoInputs(): Promise<MediaDeviceInfo[]> {
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices.filter(device => device.kind === 'videoinput');
}

function getDifferentDevice(
  videoInputs: MediaDeviceInfo[],
  currentDeviceId: string | undefined,
  nextFacingMode: CameraFacingMode
): MediaDeviceInfo | undefined {
  const otherDevices = videoInputs.filter(device => device.deviceId !== currentDeviceId);

  return (
    otherDevices.find(device => cameraLabelHints[nextFacingMode].test(device.label)) ||
    otherDevices[0]
  );
}

async function requestVideoStream(constraints: MediaTrackConstraints): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    video: constraints,
    audio: false,
  });
}

export async function getReplacementVideoStream(
  nextFacingMode: CameraFacingMode,
  currentTrack?: MediaStreamTrack
): Promise<MediaStream> {
  const currentDeviceId = currentTrack?.getSettings().deviceId;
  const videoInputs = await getVideoInputs();
  const preferredDevice = getDifferentDevice(videoInputs, currentDeviceId, nextFacingMode);

  const attempts: MediaTrackConstraints[] = [
    ...(preferredDevice ? [{ deviceId: { exact: preferredDevice.deviceId } }] : []),
    { facingMode: { exact: nextFacingMode } },
    { facingMode: { ideal: nextFacingMode } },
  ];

  let lastError: unknown;
  for (const constraints of attempts) {
    let stream: MediaStream | null = null;
    try {
      stream = await requestVideoStream(constraints);
      const track = stream.getVideoTracks()[0];
      const newDeviceId = track?.getSettings().deviceId;

      if (!currentDeviceId || !newDeviceId || newDeviceId !== currentDeviceId || videoInputs.length < 2) {
        return stream;
      }

      stream.getTracks().forEach(mediaTrack => mediaTrack.stop());
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError instanceof Error ? lastError : new Error('No alternate camera is available.');
}

export async function getReplacementVideoStreamAfterStoppingCurrent(
  nextFacingMode: CameraFacingMode,
  currentTrack?: MediaStreamTrack
): Promise<MediaStream> {
  const currentDeviceId = currentTrack?.getSettings().deviceId;
  const videoInputs = await getVideoInputs();
  const preferredDevice = getDifferentDevice(videoInputs, currentDeviceId, nextFacingMode);

  currentTrack?.stop();

  if (preferredDevice) {
    return requestVideoStream({ deviceId: { exact: preferredDevice.deviceId } });
  }

  return requestVideoStream({ facingMode: { ideal: nextFacingMode } });
}
